import React, { useCallback, useMemo, useState } from 'react';
import CameraHierarchyFilter from '../../../components/CameraHierarchyFilter/CameraHierarchyFilter';
import ViolationStateBlock from '../components/ViolationStateBlock';
import DetectionProfileSection from './DetectionProfileSection';
import AnalyticsEditPolicySection from './AnalyticsEditPolicySection';
import ZoneEditor from './ZoneEditor';
import useAnalyticsCapability from './useAnalyticsCapability';
import { submitChangeRequest } from '../../../utils/changeRequests';
import { isAdmin as checkIsAdmin } from '../../../services/api';
import { APP_EVENTS, emitAppEvent } from '../../../utils/appEvents';
import { useUrlState, HIERARCHY_URL_SCHEMA, pickHierarchy } from '../../../hooks/useUrlState';
import { useAutoDismiss } from '../../../hooks/useAutoDismiss';

/**
 * Вкладка «Аналитика камеры» (Part 4, разделы 4 и 6.2–6.3).
 *
 * Профиль детекции (режим + список детекций) вынесен в DetectionProfileSection
 * + useDetectionProfile (ТЗ задача 26) — тот же сетевой слой Task 14 переиспользует
 * в Admin → Edit Camera. Здесь остаётся только компоновка вкладки и клиентский
 * путь «заявка на изменение зоны», у которого свой notice.
 */
const AnalyticsTab = ({ onRulesChanged, onZonesChanged }) => {
  const admin = checkIsAdmin();

  // FE-FILTER02: камера в URL, чтобы можно было дать прямую ссылку на её
  // аналитику.
  const [url, setUrl] = useUrlState(HIERARCHY_URL_SCHEMA);
  const hierarchy = useMemo(() => pickHierarchy(url), [url]);
  const [notice, setNotice] = useState('');

  // FE-FIX08: «Заявка отправлена…» — success-уведомление, гаснет само.
  useAutoDismiss(notice, setNotice);

  const cameraId = hierarchy.cameraId;
  const { capability } = useAnalyticsCapability(cameraId, { isAdmin: admin });

  const proposeChange = useCallback((changeType, payload) => (
    submitChangeRequest(cameraId, changeType, payload).then((request) => {
      setNotice('Заявка отправлена администратору и ожидает одобрения');
      // ТЗ 27: новая заявка сразу поднимает pending_count — badge не должен
      // ждать смены вкладки администратором.
      emitAppEvent(APP_EVENTS.CHANGE_REQUESTS_REFRESH);
      return request;
    })
  ), [cameraId]);

  const renderBody = () => {
    if (!cameraId) {
      return <ViolationStateBlock state="empty" message="Выберите камеру, чтобы открыть её настройки." />;
    }

    return (
      <>
        {notice && !admin && <div className="vio-card"><p className="vio-notice">{notice}</p></div>}

        {admin && <DetectionProfileSection cameraId={cameraId} onRulesChanged={onRulesChanged} />}

        <ZoneEditor
          cameraId={cameraId}
          capability={capability.zones}
          onPropose={proposeChange}
          onRulesChanged={onRulesChanged}
          onZonesChanged={onZonesChanged}
        />

        {!admin && (
          <div className="vio-card">
            <p className="vio-drawer-section-title">Правила камеры</p>
            <p className="vio-field-hint">
              Правилами камеры управляет администратор. Заявку на правило подать
              нельзя — обратитесь к администратору.
            </p>
          </div>
        )}

        {admin && <AnalyticsEditPolicySection cameraId={cameraId} />}
      </>
    );
  };

  return (
    <>
      <div className="vio-card">
        <div className="vio-filters-grid">
          {/* replace: выбор камеры на этой вкладке не должен множить историю. */}
          <CameraHierarchyFilter value={hierarchy} onChange={(next) => setUrl(next)} />
        </div>
      </div>

      {renderBody()}
    </>
  );
};

export default AnalyticsTab;
